import * as assert from 'assert-plus'
import { AxiosInstance } from 'axios'
import classNames from 'classnames'
import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { Alert } from '@material-ui/lab';
import { Button } from '@material-ui/core'
import { Snackbar } from '@material-ui/core'
import ScrollAnimation from 'react-animate-on-scroll'
import ContentLoader from 'react-content-loader'
import Workbench from 'src/components/Workbench'
import Spinner from 'src/components/Spinner'
import Back from 'src/components/Back';
import { logError } from 'src/utils/Error'
import validator from 'validator'
import { APIContext, createUpdateProfileURI, getProfileURI } from '../contexts/api'
import { Auth0Context } from '../contexts/auth0'
import { useWorkspaceContext } from '../contexts/workspace'
import { IProfileProps } from '../models/views/IProfile'
import useStyles from 'src/assets/css/components/FormStyles'
import 'src/styles.css'

const Profile: FunctionComponent<IProfileProps> = (
    props: PropsWithChildren<IProfileProps>
) => {
  const { user } = useContext(Auth0Context)
  const { catalogAPI } = useContext(APIContext)
  const { setProfile } = useWorkspaceContext()
  const classes = useStyles()

  const { setViewName } = props

  const [ready, setReady] = useState(false)
  const [name, setName] = useState({ value: '', isValid: true, message: '' })
  const [email, setEmail] = useState({ value: '', isValid: true, message: '' })
  const [loading, setLoading] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [message, setMessage] = useState('Profile updated !')

  const isMounted = useRef(true)

  useEffect(() => {
    const api = catalogAPI as AxiosInstance
    assert.func(api, 'APIContext must be initialise before this component is used')

    setViewName && setViewName('Profile')
    api
      .get(getProfileURI(user.sub))
      .then(resp => {
        if (isMounted.current) {
          setName({ value: resp.data.name, isValid: true, message: '' })
          setEmail({ value: resp.data.email, isValid: true, message: '' })
          setReady(true)
        }
      })
      .catch(e => {
        if (isMounted.current) {
          setReady(true)
          logError(e)
        }
      })

    return () => {
      isMounted.current = false
    }
  }, [])

  const handleInputChanges = (e: any) => {
    e.preventDefault()
    const { name, value } = e.currentTarget
    if (name === 'name') {
      setName(prev => ({ ...prev, value }))
    } else if (name === 'email') {
      setEmail(prev => ({ ...prev, value }))
    }
  }

  const resetValidationStates = () => {
    setName(prev => ({ ...prev, isValid: true, message: '' }))
    setEmail(prev => ({ ...prev, isValid: true, message: '' }))
  }

  const formIsValid = () => {
    let isGood = true
    const newName = { ...name }
    const newEmail = { ...email }

    if (validator.isEmpty(newName.value)) {
      newName.isValid = false
      newName.message = 'Enter your name'
      isGood = false
    }

    if (!validator.isEmail(newEmail.value)) {
      newEmail.isValid = false
      newEmail.message = 'Enter valid email address'
      isGood = false
    }

    if (!isGood) {
      setName(newName)
      setEmail(newEmail)
    }

    return isGood
  }

  const submitHandler = async (e: any) => {
    e.preventDefault()
    resetValidationStates()

    if (formIsValid()) {
      const api = catalogAPI as AxiosInstance
      setSubmitSuccess(false)
      setLoading(true)
      const ProfileData = { id: user.sub, name: name.value, email: email.value }
      await api
        .put(createUpdateProfileURI(user.sub), ProfileData)
        .then(response => {
          setProfile(response.data)
          setSubmitSuccess(true)
          setLoading(false)
          setMessage('Profile updated successfully!')
        })
        .catch(e => {
          logError(e)
          setSubmitSuccess(true)
          setLoading(false)
          setMessage('Error while updating. Please try again.')
        })
    }
    return false
  }

  const nameGroupClass = classNames('form-group', { 'has-error': !name.isValid })
  const emailGroupClass = classNames('form-group', { 'has-error': !email.isValid })

  const contentLoader = (
        <div className={ready ? 'feed-hidden loaderfadeOut col' : 'feed col'}>
            <div className="dataLoader_parent d-flex" data-testid="loader">
                <div className="dataLoader col">
                    <ContentLoader className="placeholderList" viewBox="0 0 446 15" speed={2}>
                        <rect x="5" y="8" rx="0" ry="0" width="30" height="6" />
                    </ContentLoader>
                    <ContentLoader className="placeholderDivider w-100" height={10} speed={2}>
                        <rect x="0" y="5" height="2" />
                    </ContentLoader>
                    <ContentLoader viewBox="0 0 446 40" speed={2}>
                        <rect x="5" y="8" rx="0" ry="0" width="30" height="6" />
                        <rect x="5" y="18" rx="0" ry="0" width="220" height="3" />
                        <rect x="5" y="26" rx="0" ry="0" width="220" height="3" />
                    </ContentLoader>
                    <ContentLoader viewBox="0 0 446 50" speed={2}>
                        <rect x="5" y="8" rx="0" ry="0" width="30" height="6" />
                        <rect x="5" y="18" rx="0" ry="0" width="220" height="3" />
                        <rect x="5" y="26" rx="0" ry="0" width="220" height="3" />
                        <rect x="5" y="36" rx="0" ry="0" width="100" height="6" />
                    </ContentLoader>
                    <ContentLoader viewBox="0 0 446 50" speed={2}>
                        <rect x="5" y="8" rx="0" ry="0" width="30" height="6" />
                        <rect x="5" y="18" rx="0" ry="0" width="220" height="3" />
                        <rect x="5" y="26" rx="0" ry="0" width="220" height="3" />
                        <rect x="5" y="36" rx="0" ry="0" width="100" height="6" />
                    </ContentLoader>
                </div>
            </div>
        </div>
  )

  return (
        <Workbench>
            <div className="w-100" data-testid="resolved">
              <div className="full_pageContent clearfix">
                {ready ? (
                    <>
                        <Back />
                        <div className={ready ? '' : 'hidden'}>
                            <div className={classes.root}>
                                <Snackbar
                                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                  open={submitSuccess}
                                  autoHideDuration={6000}
                                  onClose={() => setSubmitSuccess(false)}>
                                    <Alert variant='filled' severity='success'>
                                      {message}
                                    </Alert>
                                </Snackbar>
                                <ScrollAnimation animateIn="fadeIn" offset={10}>
                                    <h1>Profile View</h1>
                                </ScrollAnimation>
                                <form onSubmit={submitHandler}>
                                    <ScrollAnimation animateIn="fadeIn" offset={10}>
                                        <div
                                            className={nameGroupClass}
                                        >
                                            <label className="form-label" htmlFor="profileForm.name">Name</label>
                                            <small className="text-muted form-text">
                                                We&apos;ll use your name supplied here in all correspondence.
                                            </small>
                                            <input
                                                type="text"
                                                onChange={e => handleInputChanges(e)}
                                                value={name.value}
                                                name="name"
                                                id="profileForm.name"
                                                placeholder=""
                                                className="form-control"
                                            />
                                            <small
                                                className="validation_msg"
                                                data-testid="name_err"
                                            >
                                                {name.message}
                                            </small>
                                        </div>
                                    </ScrollAnimation>

                                    { user.sub.startsWith('auth0') && (
                                    <ScrollAnimation animateIn="fadeIn" offset={10}>
                                        <div
                                            className={emailGroupClass}
                                        >
                                            <label className="form-label" htmlFor="profileForm.email">Email</label>
                                            <small className="text-muted form-text">
                                                Changing your email address will also change your login
                                                credentials.
                                            </small>
                                            <input
                                                type="text"
                                                onChange={e => handleInputChanges(e)}
                                                value={email.value}
                                                name="email"
                                                id="profileForm.email"
                                                className="form-control"
                                            />
                                            <small
                                                className="validation_msg form-text"
                                                data-testid="email_err"
                                            >
                                                {email.message}
                                            </small>
                                        </div>
                                    </ScrollAnimation>
                                    )}

                                    <div>
                                        <Button
                                            id="submit"
                                            onClick={submitHandler}
                                            variant="contained"
                                            color="primary"
                                            disabled={loading}
                                        >
                                            <Spinner active={loading}>Save</Spinner>
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </>
                ) : contentLoader}
              </div>
            </div>
        </Workbench>
  )
}

export default withRouter(Profile)
